import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getUser } from '../../utils/auth';
import { PreviewResult, emptyPair } from '../CalculationBuilder';
import { documentFromState, periodLabel } from './formulaShape';
import { distributionSummary, explainCalculationError, formatParameterValue, listStatus, projectCostPhrase } from './productCopy';
import SourceScopeEditor, { budgetCatalog, slicesFromGroups } from './SourceScopeEditor';
import AppSelect, { useAppSelectDismiss } from './AppSelect';
import { percentEntry, percentStored } from './accountantModel';
import { distributionFromAccounting } from './AccountingEditor';
import CalculationAgent from './CalculationAgent';
import CommitmentFilter from './CommitmentFilter';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function missingValue(values, key) {
  return values[key] == null || values[key] === '';
}

function destinationHint(assignment) {
  if ((assignment.job_cost_lines || []).length) {
    return 'Cost type resolves the journal account. A cost code is required when this project creates project cost.';
  }
  if (assignment.requires_cost_code === false) {
    return 'Cost type resolves the journal account. This calculation does not add project cost.';
  }
  return 'Where the resulting job cost for this group is recorded.';
}

function ProjectCostTreatment({ lines, choices, disabled, onChange }) {
  if (!lines.length) return null;
  const single = lines.length === 1;
  return (
    <fieldset className="mb-4">
      <legend className="text-sm font-medium text-gray-900 mb-2">
        {single ? 'Project Cost Treatment' : 'Project cost by journal line'}
      </legend>
      {lines.map((line) => (
        <div key={line.line_key} className="mb-3">
          {single ? null : (
            <div className="text-sm font-medium text-gray-800 mb-1">{line.side} {line.amount_label}</div>
          )}
          <label className="flex items-start gap-2 text-sm text-gray-800 mb-2">
            <input
              type="radio"
              name={`job-cost-${line.line_key}`}
              checked={choices[line.line_key] === 'create_project_cost'}
              disabled={disabled}
              onChange={() => onChange((current) => ({ ...current, [line.line_key]: 'create_project_cost' }))}
            />
            <span>
              <span className="font-medium">Create Project Cost</span>
              <span className="block text-xs font-normal text-gray-500">Creates a project expense at the configured destination in addition to the journal.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="radio"
              name={`job-cost-${line.line_key}`}
              checked={choices[line.line_key] === 'journal_only'}
              disabled={disabled}
              onChange={() => onChange((current) => ({ ...current, [line.line_key]: 'journal_only' }))}
            />
            <span>
              <span className="font-medium">Journal Only</span>
              <span className="block text-xs font-normal text-gray-500">Posts the journal but does not add the amount to project cost.</span>
            </span>
          </label>
        </div>
      ))}
    </fieldset>
  );
}

export default function ProjectCalculations() {
  const { projectVuid, assignmentVuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const inPreview = location.pathname.startsWith('/procore/');
  const role = getUser()?.role || 'user';
  const canConfigure = role !== 'viewer';
  const isAdmin = role === 'admin';
  const basePath = inPreview
    ? `/procore/projects/${projectVuid}/calculations`
    : `/projects/${projectVuid}/calculations`;

  const [assignments, setAssignments] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [assignable, setAssignable] = useState([]);
  const [choice, setChoice] = useState('');
  const [newName, setNewName] = useState('');
  const [pendingCreate, setPendingCreate] = useState(null);
  const creatingRef = useRef(false);
  const [openPicker, setOpenPicker] = useState('');
  useAppSelectDismiss(setOpenPicker);
  const [periods, setPeriods] = useState([]);
  const [periodVuid, setPeriodVuid] = useState('');
  const [costTypes, setCostTypes] = useState([]);
  const [budgetCombinations, setBudgetCombinations] = useState([]);
  const [projectCodesOnly, setProjectCodesOnly] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [values, setValues] = useState({});
  const [enabled, setEnabled] = useState(true);
  const [groups, setGroups] = useState([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const [accounts, setAccounts] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [jobCostChoices, setJobCostChoices] = useState({});
  const [excludedCommitments, setExcludedCommitments] = useState([]);
  const [projectCommitments, setProjectCommitments] = useState([]);
  const [closePolicy, setClosePolicy] = useState('manual');
  const [runInputValues, setRunInputValues] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [missing, setMissing] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmingPost, setConfirmingPost] = useState(false);
  const [posting, setPosting] = useState(false);
  const [confirmingReverse, setConfirmingReverse] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [reversalReason, setReversalReason] = useState('');
  const [history, setHistory] = useState([]);
  const [historyRunVuid, setHistoryRunVuid] = useState('');
  const [reviewingUpdate, setReviewingUpdate] = useState(false);

  const loadList = () => axios.get(`${baseURL}/api/projects/${projectVuid}/calculations`).then((response) => {
    setAssignments(response.data?.assignments || []);
  });

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    Promise.all([
      axios.get(`${baseURL}/api/projects/${projectVuid}/calculations`),
      axios.get(`${baseURL}/api/calculation-library/assignable`),
      axios.get(`${baseURL}/api/accounting-periods`),
      axios.get(`${baseURL}/api/cost-types`),
      axios.get(`${baseURL}/api/projects/${projectVuid}/settings`),
      axios.get(`${baseURL}/api/chartofaccounts`),
      axios.get(`${baseURL}/api/projects/${projectVuid}/budget-lines`),
    ]).then(([assignedRes, libraryRes, periodRes, typeRes, settingsRes, accountRes, budgetRes]) => {
      if (cancelled) return;
      setAssignments(assignedRes.data?.assignments || []);
      setAssignable(libraryRes.data?.calculations || []);
      const periodRows = Array.isArray(periodRes.data) ? periodRes.data : [];
      setPeriods(periodRows);
      const open = periodRows.find((period) => String(period.status || '').toLowerCase() === 'open');
      if (open) setPeriodVuid(open.vuid);
      setCostTypes(Array.isArray(typeRes.data) ? typeRes.data : []);
      setBudgetCombinations(budgetCatalog(budgetRes.data).combinations);
      setProjectCodesOnly(!!settingsRes.data?.allow_project_cost_codes);
      setAccounts(Array.isArray(accountRes.data) ? accountRes.data.filter((account) => account.status === 'active') : []);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Could not load project calculations.');
    }).finally(() => {
      if (!cancelled) setListLoading(false);
    });
    return () => { cancelled = true; };
  }, [projectVuid]);

  useEffect(() => {
    if (!assignmentVuid) {
      setAssignment(null);
      return undefined;
    }
    let cancelled = false;
    setAssignment(null);
    axios.get(`${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}`).then((response) => {
      if (cancelled) return;
      const body = response.data;
      setAssignment(body);
      setValues(body.parameter_values || {});
      setOverrides(body.account_overrides || {});
      setJobCostChoices(body.job_cost_choices || {});
      setExcludedCommitments(body.excluded_commitment_vuids || []);
      setClosePolicy(body.close_policy || 'manual');
      setEnabled(body.enabled !== false);
      const loadedGroups = (body.slices || []).map((slice) => {
        const pair = (slice.pairs || []).find((item) => item.cost_code_vuid === slice.destination_cost_code_vuid);
        const costType = costTypes.find((item) => item.vuid === slice.destination_cost_type_vuid);
        return {
          id: slice.vuid,
          label: slice.label || '',
          pairs: (slice.pairs || []).length ? slice.pairs : [emptyPair()],
          destination_cost_code_vuid: slice.destination_cost_code_vuid || '',
          destination_cost_type_vuid: slice.destination_cost_type_vuid || '',
          destination_cost_code_label: slice.destination_cost_code_label || pair?.cost_code_label || '',
          destination_cost_type_label: slice.destination_cost_type_label || (costType ? (costType.abbreviation || costType.cost_type || '') : ''),
        };
      });
      if (!loadedGroups.length && body.requires_job_cost && !body.requires_scope) {
        loadedGroups.push({
          id: 'destination',
          label: 'Destination',
          pairs: [],
          destination_cost_code_vuid: '',
          destination_cost_type_vuid: '',
        });
      }
      setGroups(loadedGroups);
      setExpandedGroupIds(new Set());
      setWarnings([]);
      setMissing([]);
      setPreview(null);
      setRunInputValues({});
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Could not load this assignment.');
    });
    return () => { cancelled = true; };
  }, [projectVuid, assignmentVuid]);

  useEffect(() => {
    if (!assignment?.uses_commitment_total) {
      setProjectCommitments([]);
      return undefined;
    }
    let cancelled = false;
    axios.get(`${baseURL}/api/project-commitments`, { params: { project_vuid: projectVuid } }).then((response) => {
      if (!cancelled) setProjectCommitments(Array.isArray(response.data) ? response.data : []);
    }).catch(() => {
      if (!cancelled) setProjectCommitments([]);
    });
    return () => { cancelled = true; };
  }, [assignment?.uses_commitment_total, projectVuid]);

  const createForProject = async (draft, slots) => {
    if (creatingRef.current) return;
    const name = newName.trim();
    if (!name) {
      setPendingCreate({ draft, slots });
      setError('A calculation needs a name.');
      return;
    }
    const row = slots?.destination;
    const destination = row?.cost_code_vuid ? {
      cost_code_vuid: row.cost_code_vuid,
      cost_type_vuid: row.cost_type_vuid || '',
      cost_code_label: `${row.code || ''} — ${row.description || ''}`.replace(/ — $/, ''),
      cost_type_label: row.abbreviation || row.cost_type || '',
    } : null;
    creatingRef.current = true;
    setError('');
    try {
      const response = await axios.post(`${baseURL}/api/calculation-library`, {
        name,
        description: draft?.description || '',
      });
      if (response.data.draft?.vuid) {
        await axios.put(`${baseURL}/api/calculation-library/versions/${response.data.draft.vuid}`, {
          document: documentFromState(draft?.parameters || [], draft?.namedValues || [], draft?.source, destination, draft?.calculationType),
          distribution: distributionFromAccounting(draft?.accountingLines || [], draft?.namedValues || []),
        });
      }
      const definitionVuid = response.data.definition.vuid;
      const editorPath = inPreview
        ? `/procore/setup/calculation-library/${definitionVuid}?forProject=${projectVuid}`
        : `/calculation-library/${definitionVuid}?forProject=${projectVuid}`;
      navigate(editorPath);
    } catch (err) {
      creatingRef.current = false;
      setPendingCreate({ draft, slots });
      setError(err.response?.data?.error || 'Could not create the calculation.');
    }
  };

  const addCalculation = async (event) => {
    event.preventDefault();
    setError('');
    if (!choice) return;
    try {
      const response = await axios.post(`${baseURL}/api/projects/${projectVuid}/calculations`, { definition_vuid: choice });
      await loadList();
      navigate(`${basePath}/${response.data.vuid}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add this calculation.');
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await axios.put(`${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}`, {
        enabled,
        parameter_values: values,
        account_overrides: overrides,
        job_cost_choices: jobCostChoices,
        close_policy: closePolicy,
        excluded_commitment_vuids: assignment?.uses_commitment_total ? excludedCommitments : undefined,
        slices: (assignment.requires_scope || assignment.scope_filter || assignment.requires_job_cost) ? slicesFromGroups(groups) : [],
      });
      setAssignment(response.data);
      setValues(response.data.parameter_values || {});
      setJobCostChoices(response.data.job_cost_choices || {});
      setExcludedCommitments(response.data.excluded_commitment_vuids || []);
      setClosePolicy(response.data.close_policy || 'manual');
      if (periodVuid) {
        const runResponse = await axios.get(`${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}/runs`, {
          params: { accounting_period_vuid: periodVuid },
        });
        const period = periods.find((item) => item.vuid === periodVuid);
        if (runResponse.data?.run) {
          setPreview({ ...runResponse.data, period_label: runResponse.data.period_label || periodLabel(period) });
        }
      }
      await loadList();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save project configuration.');
    } finally {
      setSaving(false);
    }
  };

  const updateVersion = async () => {
    setError('');
    setPreview(null);
    try {
      const response = await axios.post(
        `${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}/version`,
        { version_vuid: assignment.latest_published_vuid },
      );
      setAssignment(response.data);
      setValues(response.data.parameter_values || {});
      setWarnings(response.data.warnings || []);
      setMissing(response.data.missing_parameters || []);
      await loadList();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update the version.');
    }
  };

  const runPreview = async () => {
    setError('');
    setConfirmingPost(false);
    setPreview(null);
    try {
      const response = await axios.post(
        `${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}/preview`,
        {
          accounting_period_vuid: periodVuid,
          run_inputs: (assignment.run_inputs || []).map((provider) => ({
            key: provider.key,
            value: runInputValues[provider.key] || '',
          })),
        },
      );
      const period = periods.find((item) => item.vuid === periodVuid);
      setPreview({ ...response.data, period_label: periodLabel(period) });
    } catch (err) {
      setError(err.response?.data?.error || 'Preview failed.');
    }
  };

  useEffect(() => {
    if (!assignmentVuid || !periodVuid) return undefined;
    let cancelled = false;
    setPeriodLoading(true);
    axios.get(`${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}/runs`, {
      params: {
        accounting_period_vuid: periodVuid,
        run_vuid: historyRunVuid || undefined,
      },
    }).then((response) => {
      if (cancelled) return;
      setHistory(response.data?.history || []);
      if (!response.data?.run) {
        setPreview(null);
        if (!historyRunVuid) setRunInputValues({});
        return;
      }
      const period = periods.find((item) => item.vuid === periodVuid);
      setPreview({ ...response.data, period_label: response.data.period_label || periodLabel(period) });
      const inputs = {};
      (response.data.shared_inputs || []).forEach((input) => {
        if (input.provider_key) inputs[input.provider_key] = input.value ?? '';
      });
      setRunInputValues(inputs);
    }).catch(() => {}).finally(() => {
      if (!cancelled) setPeriodLoading(false);
    });
    return () => { cancelled = true; };
  }, [assignmentVuid, periodVuid, projectVuid, periods, historyRunVuid]);

  const posted = preview?.run?.status === 'posted';
  const reversed = preview?.run?.status === 'reversed';
  const lockedRun = posted || reversed;
  const projectCostCount = (preview?.accounting?.lines || []).filter((line) => line.creates_project_cost).length;
  const journalOnly = !!assignment?.journal_only && !(assignment?.run_inputs || []).length;
  const wipPath = inPreview ? '/procore/reports/wip' : '/wip';
  const chargesCreated = preview?.run?.status === 'charges_created';
  const canPost = canConfigure && projectCostCount === 0 && preview?.run?.status === 'ready' && !preview?.accounting?.nothing_to_post && !(preview?.accounting?.errors || []).length;
  const chargesPath = location.pathname.startsWith('/procore/')
    ? `/procore/projects/${projectVuid}/project-expenses?project=${projectVuid}`
    : `/project-expenses?project=${projectVuid}`;

  const reverseRun = async () => {
    if (!preview?.run?.vuid || !reversalReason.trim()) return;
    setError('');
    setReversing(true);
    try {
      const response = await axios.post(
        `${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}/runs/${preview.run.vuid}/reverse`,
        { reason: reversalReason.trim() },
      );
      const period = periods.find((item) => item.vuid === periodVuid);
      setHistory(response.data?.history || []);
      setHistoryRunVuid(response.data?.run?.vuid || '');
      setPreview({ ...response.data, period_label: response.data.period_label || periodLabel(period) });
      setConfirmingReverse(false);
      setReversalReason('');
      setRunInputValues({});
      await loadList();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reverse this calculation.');
    } finally {
      setReversing(false);
    }
  };

  const postRun = async () => {
    if (!preview?.run?.vuid) return;
    setError('');
    setPosting(true);
    try {
      const response = await axios.post(
        `${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}/runs/${preview.run.vuid}/post`,
      );
      const period = periods.find((item) => item.vuid === periodVuid);
      setPreview({ ...response.data, period_label: response.data.period_label || periodLabel(period) });
      setConfirmingPost(false);
      await loadList();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not post this calculation.');
    } finally {
      setPosting(false);
    }
  };

  const discardRun = async () => {
    if (!preview?.run?.vuid) return;
    setError('');
    try {
      await axios.delete(`${baseURL}/api/projects/${projectVuid}/calculations/${assignmentVuid}/runs/${preview.run.vuid}`);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not discard this calculation run.');
    }
  };

  const currentPeriod = periods.find((period) => period.vuid === periodVuid);
  const taken = new Set(assignments.map((row) => row.definition_vuid));
  const available = assignable.filter((row) => !taken.has(row.vuid));
  const chosen = available.find((row) => row.vuid === choice);

  if (!assignmentVuid && listLoading) {
    return (
      <div className="min-h-[16rem] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vermillion-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project calculations...</p>
        </div>
      </div>
    );
  }

  if (!assignmentVuid) {
    return (
      <div className={inPreview ? 'pc-calc pc-calc-list' : undefined}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Calculations</h1>
            <p className="text-sm text-gray-600 mt-1">
              Setup is saved on the project. Each period is calculated, reviewed, and posted on its own.
              {currentPeriod ? ` Current period: ${periodLabel(currentPeriod)}.` : ''}
            </p>
          </div>
        </div>
        {error ? <div className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div> : null}
        {isAdmin ? (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-3 pc-calc-create">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Create a calculation</h2>
            <label className="pc-calc-create-name text-sm font-medium text-gray-700 mb-3">
              Name
              <input
                className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-md"
                placeholder="Name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
              />
            </label>
            <CalculationAgent
              asking
              name={newName}
              setName={setNewName}
              description=""
              setDescription={() => {}}
              parameters={[]}
              setParameters={() => {}}
              namedValues={[]}
              setNamedValues={() => {}}
              accountingLines={[]}
              setAccountingLines={() => {}}
              accounts={accounts}
              costTypes={costTypes}
              source={null}
              setSource={() => {}}
              costCodes={budgetCombinations}
              destination={null}
              onDestination={() => {}}
              onBuilt={createForProject}
              finishLabel="Create"
            />
            {pendingCreate ? (
              <button type="button" className="btn-claret mt-3" onClick={() => createForProject(pendingCreate.draft, pendingCreate.slots)}>Create</button>
            ) : null}
          </div>
        ) : null}
        {canConfigure ? (
          <form className="bg-white rounded-xl shadow-lg p-6 mb-6" onSubmit={addCalculation}>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Add a published calculation</h2>
            <p className="text-sm text-gray-600 mb-3">Use a method that is already in the Calculation Library.</p>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="min-w-[16rem] flex-1 max-w-md">
                <AppSelect
                  id="add-calculation"
                  open={openPicker === 'add-calculation'}
                  setOpen={setOpenPicker}
                  value={chosen ? `${chosen.name} — v${chosen.version_number}` : ''}
                  placeholder="Choose a published calculation"
                  searchPlaceholder="Search calculations"
                  options={available.map((row) => ({
                    vuid: row.vuid,
                    primary: row.name,
                    secondary: `Version ${row.version_number}`,
                  }))}
                  onSelect={setChoice}
                  disabled={!available.length}
                />
              </div>
              <button type="submit" className="btn-claret" disabled={!choice}>Add to this project</button>
            </div>
            {!available.length ? (
              <p className="text-sm text-gray-600 mt-3">Every published calculation is already on this project, or the library has none yet.</p>
            ) : null}
          </form>
        ) : null}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Calculation</th>
                <th className="px-4 py-3 font-medium">Version</th>
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((row) => (
                <tr key={row.vuid} className="border-t border-gray-200">
                  <td className="px-4 py-3">
                    <button type="button" className="font-medium text-gray-900 underline" onClick={() => navigate(`${basePath}/${row.vuid}`)}>
                      {row.definition_name}
                    </button>
                    {row.update_available ? <div className="text-xs text-amber-800">Version {row.latest_published_number} available</div> : null}
                  </td>
                  <td className="px-4 py-3">v{row.version_number}</td>
                  <td className="px-4 py-3">{currentPeriod ? periodLabel(currentPeriod) : '—'}</td>
                  <td className="px-4 py-3">
                    {listStatus(row)}
                    {row.period_reversed_count && row.period_run_status !== 'reversed' ? (
                      <div className="text-xs text-gray-500">Includes reversed history</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right">{row.period_amount_display || '—'}</td>
                  <td className="px-4 py-3">
                    <button type="button" className="underline" onClick={() => navigate(`${basePath}/${row.vuid}`)}>Open</button>
                  </td>
                </tr>
              ))}
              {!assignments.length ? (
                <tr>
                  <td className="px-4 py-6 text-gray-700" colSpan={6}>
                    <div className="font-medium text-gray-900">No calculations assigned to this project</div>
                    <div className="mt-1">This project does not have any calculations configured.</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="min-h-[16rem] flex items-center justify-center">
        <div className="text-center">
          {error ? <p className="text-sm text-red-800">{error}</p> : (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vermillion-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading this calculation...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const parameterMissing = (assignment.parameters || []).filter((item) => (
    item.required !== false && missingValue(values, item.key) && (item.default_value == null || item.default_value === '')
  ));
  const missingInputs = (assignment.run_inputs || []).filter((item) => !String(runInputValues[item.key] || '').trim());
  const periodName = preview?.period_label || periodLabel(currentPeriod);
  const currentLines = distributionSummary(assignment.distribution, assignment.named_values);
  const latest = assignment.latest_published;
  const showCurrentInHistory = preview?.run && preview.run.status !== 'reversed';
  const accountName = (vuid) => {
    const account = accounts.find((item) => item.vuid === vuid);
    return account ? `${account.account_number} — ${account.account_name}` : '';
  };
  const editSetup = () => {
    document.getElementById('project-cost-destination')?.scrollIntoView({ behavior: 'smooth' });
  };
  const startCorrection = () => {
    setHistoryRunVuid('');
    setConfirmingReverse(false);
    setReversalReason('');
    document.getElementById('period-calculation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={inPreview ? 'pc-calc pc-calc-detail' : undefined}>
      <button type="button" className="text-sm underline mb-4" onClick={() => navigate(basePath)}>Back to Project Calculations</button>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{assignment.definition_name}</h1>
      <p className="text-sm text-gray-600 mb-4">Version {assignment.version_number}. This project stays on this version until you apply a newer one.</p>
      {assignment.update_available ? (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Version {assignment.latest_published_number} available. This project is not out of date until you choose to update it.
          {canConfigure && !reviewingUpdate ? (
            <button type="button" className="btn-claret ml-3" onClick={() => setReviewingUpdate(true)}>Review Update</button>
          ) : null}
          {reviewingUpdate ? (
            <div className="mt-3 bg-white rounded-md p-3 text-gray-900">
              <div className="font-medium mb-2">Version {assignment.version_number} on this project</div>
              {currentLines.map((line, index) => (
                <div key={`current-${index}`}>{line.side} {line.amount} — {line.account}{projectCostPhrase(line) ? ` — ${projectCostPhrase(line)}` : ''}</div>
              ))}
              {!currentLines.length ? <div>No accounting method on this version.</div> : null}
              <div className="font-medium mt-3 mb-2">Version {latest?.version_number}</div>
              {(latest?.parameters || []).map((item) => <div key={item.key}>{item.label}</div>)}
              {(latest?.calculated_values || []).map((item) => <div key={item.key}>Calculation: {item.label}</div>)}
              {(latest?.lines || []).map((line, index) => (
                <div key={`next-${index}`}>{line.side} {line.amount} — {line.account}{projectCostPhrase(line) ? ` — ${projectCostPhrase(line)}` : ''}</div>
              ))}
              <div className="mt-3 flex gap-3">
                <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm" onClick={() => setReviewingUpdate(false)}>Cancel</button>
                <button type="button" className="btn-claret" onClick={() => { setReviewingUpdate(false); updateVersion(); }}>Update to v{assignment.latest_published_number}</button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {warnings.length ? (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {warnings.map((text) => <div key={text}>{text}</div>)}
        </div>
      ) : null}
      {missing.length ? (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Fill in before calculating: {missing.join(', ')}
        </div>
      ) : null}
      {error ? <div className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{explainCalculationError(error)}</div> : null}

      <section id="project-setup" className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Project Setup</h2>
        <p className="text-sm text-gray-600 mb-4">Configured once for this project. It does not change the library or any other project.</p>
        <label className="inline-flex items-center gap-2 text-sm text-gray-800 mb-4">
          <input type="checkbox" checked={enabled} disabled={!canConfigure} onChange={(event) => setEnabled(event.target.checked)} />
          Enabled
        </label>
        <fieldset className="mb-4">
          <legend className="text-sm font-medium text-gray-900 mb-2">Month-End Behavior</legend>
          <label className="flex items-start gap-2 text-sm text-gray-800 mb-2">
            <input type="radio" name="close-policy" checked={closePolicy === 'manual'} disabled={!canConfigure} onChange={() => setClosePolicy('manual')} />
            <span>
              <span className="font-medium">Manual</span>
              <span className="block text-xs font-normal text-gray-500">{journalOnly ? 'Included when you post project journals.' : 'Calculate and post this calculation manually.'}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-800 mb-2">
            <input type="radio" name="close-policy" checked={closePolicy === 'required_before_close'} disabled={!canConfigure} onChange={() => setClosePolicy('required_before_close')} />
            <span>
              <span className="font-medium">Required Before Close</span>
              <span className="block text-xs font-normal text-gray-500">WIP cannot close until this calculation has been posted or has a valid zero result.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input type="radio" name="close-policy" checked={closePolicy === 'post_during_close'} disabled={!canConfigure} onChange={() => setClosePolicy('post_during_close')} />
            <span>
              <span className="font-medium">Post During Close</span>
              <span className="block text-xs font-normal text-gray-500">Prepare and review the calculation before close. WIP close posts the reviewed calculation automatically.</span>
            </span>
          </label>
        </fieldset>
        {assignment.uses_commitment_total ? (
          <CommitmentFilter
            id="commitment-filter"
            open={openPicker}
            setOpen={setOpenPicker}
            commitments={projectCommitments}
            excluded={excludedCommitments}
            onChange={setExcludedCommitments}
            disabled={!canConfigure}
          />
        ) : null}
        {(assignment.parameters || []).map((item) => {
          const stored = values[item.key] ?? '';
          const shown = item.display === 'percent' ? percentEntry(stored) : stored;
          return (
          <label key={item.key} className="block text-sm font-medium text-gray-700 mb-3">
            {item.label}
            <span className="mt-1 flex items-center gap-2">
              <input
                className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md"
                value={shown}
                disabled={!canConfigure}
                placeholder={item.default_value ? formatParameterValue(item.default_value, item.display) : ''}
                onChange={(event) => {
                  const next = item.display === 'percent' ? percentStored(event.target.value) : event.target.value;
                  setValues((current) => ({ ...current, [item.key]: next }));
                }}
              />
              {item.display === 'percent' ? <span className="text-sm text-gray-600">%</span> : null}
              {item.display === 'per_thousand' ? <span className="text-sm text-gray-600">per $1,000</span> : null}
            </span>
            <span className="mt-1 block text-xs font-normal text-gray-500">
              Each project can use its own {item.label}.
              {item.default_value ? ` The default is ${formatParameterValue(item.default_value, item.display)}.` : ''} Changing this does not change the library.
            </span>
          </label>
          );
        })}
        <ProjectCostTreatment
          lines={assignment.job_cost_lines || []}
          choices={jobCostChoices}
          disabled={!canConfigure}
          onChange={setJobCostChoices}
        />
        {parameterMissing.length ? (
          <p className="text-sm text-amber-800">Needs a value: {parameterMissing.map((item) => item.label).join(', ')}</p>
        ) : null}
        {(assignment.account_roles || []).map((role) => (
          <label key={role.role_key} className="block text-sm font-medium text-gray-700 mb-3">
            {role.role_label}
            <div className="mt-1 w-full md:w-96">
              <AppSelect
                id={`account-${role.role_key}`}
                open={openPicker === `account-${role.role_key}`}
                setOpen={setOpenPicker}
                value={overrides[role.role_key]
                  ? (accountName(overrides[role.role_key]) || '')
                  : (role.default_account_vuid ? `Using library default: ${accountName(role.default_account_vuid) || 'configured account'}` : 'Using the company default')}
                placeholder="Choose an account"
                searchPlaceholder="Search accounts"
                layout="stacked"
                disabled={!canConfigure}
                options={[
                  {
                    vuid: '',
                    primary: role.default_account_vuid ? `Using library default: ${accountName(role.default_account_vuid) || 'configured account'}` : 'Using the company default',
                  },
                  ...accounts.map((account) => ({
                    vuid: account.vuid,
                    primary: account.account_number || account.account_name,
                    secondary: account.account_number ? account.account_name : '',
                  })),
                ]}
                onSelect={(vuid) => setOverrides((current) => ({ ...current, [role.role_key]: vuid }))}
              />
            </div>
            <span className="mt-1 block text-xs font-normal text-gray-500">
              {overrides[role.role_key]
                ? `Override: ${accountName(overrides[role.role_key])}`
                : 'Optional project override. Other projects are unchanged.'}
            </span>
          </label>
        ))}
      </section>

      {assignment.requires_scope || assignment.scope_filter ? (
        <SourceScopeEditor
          projectVuid={projectVuid}
          projectCodesOnly={projectCodesOnly}
          costTypes={costTypes}
          budgetCombinations={budgetCombinations}
          groups={groups}
          expandedGroupIds={expandedGroupIds}
          setGroups={setGroups}
          setExpandedGroupIds={setExpandedGroupIds}
          intro={assignment.scope_filter && !assignment.requires_scope
            ? 'Owner billing includes every posted cost code. Add cost codes only when this calculation should use a smaller set.'
            : 'Which costs should be included, and where the calculated project cost should be posted. Only cost code and cost type combinations on this project\'s budget are listed.'}
          readOnly={!canConfigure}
          showDestination={!!assignment.requires_job_cost}
          showCostCode={assignment.requires_cost_code !== false}
          destinationHint={destinationHint(assignment)}
          filterOptional={!!assignment.scope_filter && !assignment.requires_scope}
        />
      ) : null}
      {!assignment.requires_scope && !assignment.scope_filter && assignment.requires_job_cost ? (
        <SourceScopeEditor
          projectVuid={projectVuid}
          projectCodesOnly={projectCodesOnly}
          costTypes={costTypes}
          budgetCombinations={budgetCombinations}
          groups={groups}
          expandedGroupIds={expandedGroupIds}
          setGroups={setGroups}
          setExpandedGroupIds={setExpandedGroupIds}
          intro="This calculation does not use a source. Choose the budget cost code and cost type where the project cost should be posted."
          readOnly={!canConfigure}
          showSources={false}
          showDestination
          showCostCode={assignment.requires_cost_code !== false}
          destinationHint={destinationHint(assignment)}
          allowAdd={false}
        />
      ) : null}

      {canConfigure ? (
        <button type="button" className="btn-claret mb-6" onClick={save} disabled={saving}>Save Project Setup</button>
      ) : null}

      <section id="period-calculation" className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">This Period</h2>
        <p className="text-sm text-gray-600 mb-4">{journalOnly
          ? 'This journal is included with project journals on the WIP report, the same way over/under billing is. It does not create a project charge, and it does not have a separate calculate step.'
          : 'Calculated, reviewed, and posted for one accounting period. Setup above is not changed by posting.'}</p>
        {history.length && !preview?.run ? (
          <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="font-medium">Correcting {periodName}</div>
            <div className="mt-1">Original calculation: {history[0].total_debits_display} — Reversed{history[0].reversal_reason ? ` — ${history[0].reversal_reason}` : ''}</div>
            <div>Current version: v{assignment.version_number}</div>
            <div className="mt-1">Enter this period&apos;s amounts again. The reversed calculation is not reused.</div>
          </div>
        ) : null}
        {posted ? (
          <p className="text-sm text-gray-700 mb-4">Posted. The review below is the calculation that was posted. It cannot be recalculated.</p>
        ) : null}
        {reversed ? (
          <p className="text-sm text-gray-700 mb-4">Reversed. The original calculation and journal remain in history.</p>
        ) : null}
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Accounting period
          <div className="mt-1 w-full md:w-64">
            <AppSelect
              id="period"
              open={openPicker === 'period'}
              setOpen={setOpenPicker}
              value={(() => {
                const period = periods.find((row) => row.vuid === periodVuid);
                return period ? periodLabel(period) : '';
              })()}
              placeholder="Choose a period"
              searchPlaceholder="Search periods"
              options={periods.map((period) => ({
                vuid: period.vuid,
                primary: periodLabel(period),
                secondary: period.status ? String(period.status) : '',
              }))}
              onSelect={(vuid) => {
                setConfirmingPost(false);
                setConfirmingReverse(false);
                setReviewingUpdate(false);
                setHistoryRunVuid('');
                setPeriodVuid(vuid);
              }}
            />
          </div>
        </label>
        {!lockedRun && (assignment.run_inputs || []).length ? (
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Amount for this period</h3>
            {(assignment.run_inputs || []).map((provider) => (
              <label key={provider.key} className="block text-sm font-medium text-gray-700 mb-3">
                {provider.key === 'manual_amount' ? `${assignment.name} amount` : provider.name}
                <span className="ml-2 text-xs font-normal text-amber-800">Required for {periodName}</span>
                <input
                  className="mt-1 w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md"
                  value={runInputValues[provider.key] || ''}
                  disabled={!canConfigure || !!historyRunVuid}
                  onChange={(event) => setRunInputValues((current) => ({ ...current, [provider.key]: event.target.value }))}
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  {provider.key === 'manual_amount'
                    ? 'Enter this amount for the selected accounting period.'
                    : provider.description}
                </span>
              </label>
            ))}
            {missingInputs.length && !historyRunVuid ? (
              <p className="text-sm text-amber-800">Enter {missingInputs.map((item) => item.name).join(', ')} to calculate this period.</p>
            ) : null}
          </div>
        ) : null}
        {history.length || showCurrentInHistory ? (
          <div className="mb-4 rounded border border-gray-200 p-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{periodName}</h3>
            {[...history].reverse().map((item) => (
              <div key={item.vuid} className="text-sm text-gray-800 mb-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span>v{item.version_number} — {item.total_debits_display} — Reversed</span>
                  <button type="button" className="underline" onClick={() => { setConfirmingPost(false); setConfirmingReverse(false); setHistoryRunVuid(item.vuid); }}>View</button>
                </div>
                {item.reversed_at ? (
                  <div className="text-xs text-gray-500">Reversed {new Date(item.reversed_at).toLocaleDateString()}{item.reversal_reason ? ` — ${item.reversal_reason}` : ''}</div>
                ) : null}
              </div>
            ))}
            {showCurrentInHistory ? (
              <div className="text-sm text-gray-800">
                v{preview.run.version_number || assignment.version_number} — {preview.accounting?.total_debits_display || '—'} — {preview.run.status === 'posted' ? 'Posted' : (preview.accounting?.nothing_to_post ? 'Nothing to post' : (preview.run.status === 'ready' ? 'Ready to Post' : 'In progress'))}
              </div>
            ) : null}
            {historyRunVuid ? (
              <button type="button" className="underline text-sm mt-2" onClick={() => setHistoryRunVuid('')}>Show the current period</button>
            ) : null}
          </div>
        ) : null}
        {(listLoading || periodLoading) && !preview?.run && !historyRunVuid ? (
          <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-vermillion-600"></div>
            Loading this period...
          </div>
        ) : null}
        {!journalOnly && !listLoading && !periodLoading && !preview?.run && !historyRunVuid ? (
          <p className="text-sm text-gray-700 mb-3">{assignment.definition_name} has not been calculated for {periodName}.</p>
        ) : null}
        {journalOnly ? (
          <p className="text-sm text-gray-700 mb-3">
            Open the WIP report and use Post Project Journals to preview this journal with over/under billing before anything is posted.
            {' '}
            <Link to={wipPath} className="underline text-blue-800">Open WIP Report</Link>
          </p>
        ) : null}
        {journalOnly || lockedRun || historyRunVuid || ((listLoading || periodLoading) && !preview?.run) ? null : (
          <button type="button" className="btn-claret" onClick={runPreview} disabled={!periodVuid || !canConfigure || missingInputs.length > 0}>
            {preview?.run?.recalculate_available ? 'Recalculate' : 'Calculate'}
          </button>
        )}
        {reversed && canConfigure ? (
          <div className="mt-4">
            <button type="button" className="btn-claret" onClick={startCorrection}>Create Corrected Calculation</button>
          </div>
        ) : null}
        {preview?.run?.assignment_version_changed && canConfigure && !lockedRun ? (
          <div className="mt-3">
            <button type="button" className="btn-claret" onClick={discardRun}>Discard this run</button>
          </div>
        ) : null}
      </section>
      {preview ? <PreviewResult preview={preview} onEditSetup={editSetup} /> : null}
      {projectCostCount > 0 && (preview?.run?.status === 'ready' || chargesCreated) ? (
        <p className="mt-4 text-sm text-gray-700">
          These job-costed amounts are created as approved project charges. Preview them on Project Charges, create the ones you want, then post those charges with the others.
          {' '}
          <Link to={chargesPath} className="underline text-blue-800">Open Project Charges</Link>
        </p>
      ) : null}
      {canPost && !journalOnly ? (
        <p className="mt-4 text-sm text-gray-700">This journal posts from Post Project Journals on the WIP report. You can also post it here.</p>
      ) : null}
      {canPost && !journalOnly && !confirmingPost ? (
        <div className="mt-3">
          <button type="button" className="btn-claret" onClick={() => setConfirmingPost(true)}>Post Calculation</button>
        </div>
      ) : null}
      {canPost && !journalOnly && confirmingPost ? (
        <div className="mt-4 rounded border border-gray-300 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Post {assignment.definition_name}?</h3>
          <div className="text-sm text-gray-800 space-y-1">
            <div>Project: {assignment.project_name || 'This project'}</div>
            <div>Calculation: {assignment.definition_name} — v{preview.run.version_number || assignment.version_number}</div>
            <div>Period: {preview.period_label || periodLabel(currentPeriod)}</div>
            <div className="pt-2">Journal:</div>
            <div>Debits: {preview.accounting.total_debits_display}</div>
            <div>Credits: {preview.accounting.total_credits_display}</div>
            <div>Result: {preview.accounting.total_debits_display}</div>
            <div>Project cost to create: {projectCostCount}</div>
          </div>
          <p className="mt-3 text-sm text-gray-700">This posts the calculation you reviewed. The journal and project cost stay as shown.</p>
          <div className="mt-4 flex gap-3">
            <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm" onClick={() => setConfirmingPost(false)} disabled={posting}>Cancel</button>
            <button type="button" className="btn-claret" onClick={postRun} disabled={posting}>Post Calculation</button>
          </div>
        </div>
      ) : null}
      {posted && canConfigure && !confirmingReverse ? (
        <div className="mt-4">
          <button type="button" className="btn-claret" onClick={() => { setConfirmingPost(false); setConfirmingReverse(true); }}>Reverse Calculation</button>
        </div>
      ) : null}
      {posted && canConfigure && confirmingReverse ? (
        <div className="mt-4 rounded border border-gray-300 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Reverse {assignment.definition_name}?</h3>
          <div className="text-sm text-gray-800 space-y-1">
            <div>Project: {assignment.project_name || 'This project'}</div>
            <div>Calculation: {assignment.definition_name} — v{preview.run.version_number || assignment.version_number}</div>
            <div>Period: {preview.period_label || periodLabel(currentPeriod)}</div>
            <div className="pt-2">Original journal:</div>
            <div>Debits: {preview.accounting.total_debits_display}</div>
            <div>Credits: {preview.accounting.total_credits_display}</div>
            <div>Project costs to reverse: {projectCostCount}</div>
          </div>
          <label className="block text-sm font-medium text-gray-700 mt-3">
            Reason for reversal
            <textarea
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              value={reversalReason}
              onChange={(event) => setReversalReason(event.target.value)}
            />
          </label>
          <p className="mt-3 text-sm text-gray-700">Creates equal-and-opposite accounting. The original calculation remains in history.</p>
          <div className="mt-4 flex gap-3">
            <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm" onClick={() => setConfirmingReverse(false)} disabled={reversing}>Cancel</button>
            <button type="button" className="btn-claret" onClick={reverseRun} disabled={reversing || !reversalReason.trim()}>Reverse Calculation</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
